import * as React from 'react';
import {
  IRatioReportState,
  ratioReportProvider,
} from '../../../data/providers/reports/ratioReportProvider';
import { Entity, EntityType } from '../../../data/types';
import { Column } from '../../layout/Column';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { EPdfMode, isPdf, Pdf } from '../../shared/pdf/Pdf';
import { PdfContainer } from '../../shared/pdf/PdfContainer';
import { GraphCard } from '../ratio/GraphCard';
import { ResultList } from '../ratio/ResultList';
import { ErrorMessage } from '../utils/ErrorMessages';
import { ReportOverview } from '../utils/ReportOverview';
import { useFilename } from '../utils/useFilename';
import { RatioSettings } from './settings/Settings';

interface IProps {
  pdfMode?: EPdfMode;
  entity: Entity;
  entityType: EntityType;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
}

export const RatioReport = (props: IProps): React.ReactElement => {
  const [reportState, setReportState] = React.useState<IRatioReportState>();
  /**
   * @description subscribe to report state
   */
  React.useEffect((): (() => void) => {
    props.setLoading(true);
    const subscriber = ratioReportProvider(
      props.entityType,
      props.entity.id
    ).subscribe((state: IRatioReportState): void => {
      setReportState(state);
      props.setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, [props.entity?.id]);

  /**
   * @description make filename for export
   */
  const filename = useFilename(
    `ratio_${reportState?.settings?.name}`,
    props.entity,
    reportState?.timeframe
  );

  return (
    <React.Fragment>
      {reportState?.isValid && !props.loading ? (
        isPdf(props.pdfMode) ? (
          <PdfContainer>
            <Pdf
              onComplete={() => {}}
              filename={filename}
              title='Ratio Report'
              entity={props.entity}
              loading={props.loading}
              reportState={reportState}
              onPdfModeChange={props.onPdfModeChange}
              pdfMode={props.pdfMode}
            >
              <GraphCard pdfMode={props.pdfMode} reportState={reportState} />
              <ResultList
                reportState={reportState}
                pdfMode={props.pdfMode}
                entity={props.entity}
              />
            </Pdf>
          </PdfContainer>
        ) : (
          <Column>
            {!props.loading ? (
              <React.Fragment>
                <ReportOverview
                  reportName={`Ratio Report - ${reportState?.settings?.name}`}
                  noComparison
                />
                <GraphCard pdfMode={props.pdfMode} reportState={reportState} />
                <ResultList
                  reportState={reportState}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                />
              </React.Fragment>
            ) : (
              <LinearLoading />
            )}
          </Column>
        )
      ) : props.loading ? (
        <LinearLoading />
      ) : (
        <Error
          errorCode={reportState?.errorCode}
          setLoading={props.setLoading}
        />
      )}
    </React.Fragment>
  );
};

const Error = ({
  errorCode,
  setLoading,
}: {
  errorCode?: string;
  setLoading: (v: boolean) => void;
}): React.ReactElement => {
  const onSave = (): void => setLoading(true);
  switch (errorCode) {
    case 'missing-template':
      return (
        <ErrorMessage iconName='window' text='Please Select a Template'>
          <RatioSettings
            style={{ marginTop: '10px', maxWidth: 'max-content' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );
    case 'missing-date-range':
      return (
        <ErrorMessage
          text='Please select a valid timeframe for this report'
          iconName='date_range'
        >
          <RatioSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );
    case 'insufficient-data':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <RatioSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );

    default:
      return null;
  }
};
